import { type Middleware, configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { rtkQueryErrorLogger } from './rtkQueryErrorLogger'
import { coreApi, publicApi } from './services/coreApi'
import sessionReducer, {
  endSession,
  sessionInitialState,
  startImpersonationSession,
  stopImpersonationSession,
} from './slices/sessionSlice'
import {
  affiliatePartnersTableReducer,
  affiliateStatsMonthlyTableReducer,
  bankAccountsTableReducer,
  bookkeepingTableReducer,
  clientsTableReducer,
  currenciesTableReducer,
  feeStructuresTableReducer,
  finanstilsynetBreakdownTableReducer,
  finanstilsynetSamplingTableReducer,
  fundDepositsTableReducer,
  initialState,
  networksTableReducer,
  systemWalletsTableReducer,
  teamTableReducer,
  transfersTableReducer,
  veliClientsTableReducer,
  veliWealthManagersTableReducer,
} from './slices/tableFiltersSlice'

const localStorageMiddleware: Middleware = ({ getState }) => {
  return next => action => {
    const result = next(action)
    localStorage.setItem(
      'applicationState',
      JSON.stringify({
        currenciesTable: getState().currenciesTable,
        transfersTable: getState().transfersTable,
        fundDepositsTable: getState().fundDepositsTable,
        clientsTable: getState().clientsTable,
        systemWalletsTable: getState().systemWalletsTable,
        bankAccountsTable: getState().bankAccountsTable,
        networksTable: getState().networksTable,
        affiliatePartnersTable: getState().affiliatePartnersTable,
        affiliateStatsMonthlyTable: getState().affiliateStatsMonthlyTable,
        bookkeepingTable: getState().bookkeepingTable,
        feeStructuresTable: getState().feeStructuresTable,
        finanstilsynetSamplingTable: getState().finanstilsynetSamplingTable,
        finanstilsynetBreakdownTable: getState().finanstilsynetBreakdownTable,
        veliWealthManagersTable: getState().veliWealthManagersTable,
        veliClientsTable: getState().veliClientsTable,
        teamTable: getState().teamTable,
      })
    )
    return result
  }
}

// Impersonation is otherwise in-memory only, so a browser refresh would drop
// the admin out of the loop. We persist a minimal snapshot to sessionStorage
// (per-tab, cleared on tab close) so it survives refreshes. We intentionally do
// NOT persist the admin's bearer token — Auth0 re-mints that on load.
const IMPERSONATION_STORAGE_KEY = 'impersonationSession'

// Logout redirects through Auth0 in the same tab, where sessionStorage
// survives, so clear the snapshot explicitly to ensure logout ends
// impersonation rather than letting a same-tab re-login resume it.
export const clearImpersonationSnapshot = () =>
  sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY)

const impersonationPersistenceMiddleware: Middleware = ({ getState }) => {
  return next => action => {
    const result = next(action)
    if (
      action.type === startImpersonationSession.type ||
      action.type === stopImpersonationSession.type ||
      action.type === endSession.type
    ) {
      const session = getState().session
      if (session.isImpersonating && session.impersonationToken) {
        sessionStorage.setItem(
          IMPERSONATION_STORAGE_KEY,
          JSON.stringify({
            impersonationToken: session.impersonationToken,
            impersonationExpiresAtUtc: session.impersonationExpiresAtUtc,
            id: session.id,
            role: session.role,
            adminReturnPath: session.adminReturnPath,
          })
        )
      } else {
        sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY)
      }
    }
    return result
  }
}

const reHydrateImpersonation = () => {
  const raw = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY)
  if (!raw) return undefined
  try {
    const parsed = JSON.parse(raw)
    // Ignore incomplete snapshots — without a token and a target id we can
    // neither authenticate the impersonation nor fetch the impersonated user.
    if (!parsed?.impersonationToken || !parsed?.id) {
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY)
      return undefined
    }
    // Drop sessions that have already expired so we fall back cleanly to the
    // admin identity instead of getting stuck on a dead impersonation token.
    if (
      parsed.impersonationExpiresAtUtc &&
      new Date(parsed.impersonationExpiresAtUtc).getTime() <= Date.now()
    ) {
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY)
      return undefined
    }
    return {
      ...sessionInitialState,
      isImpersonating: true,
      impersonationToken: parsed.impersonationToken,
      impersonationExpiresAtUtc: parsed.impersonationExpiresAtUtc ?? '',
      id: parsed.id,
      role: parsed.role,
      adminReturnPath: parsed.adminReturnPath ?? '',
    }
  } catch {
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY)
    return undefined
  }
}

const reHydrateStore = () => {
  const session = reHydrateImpersonation()

  if (localStorage.getItem('applicationState') !== null) {
    const parsed = JSON.parse(localStorage.getItem('applicationState') ?? '') // re-hydrate the store
    const newState = {
      currenciesTable: { ...initialState, ...parsed?.currenciesTable },
      transfersTable: { ...initialState, ...parsed?.transfersTable },
      fundDepositsTable: { ...initialState, ...parsed?.fundDepositsTable },
      clientsTable: { ...initialState, ...parsed?.clientsTable },
      systemWalletsTable: { ...initialState, ...parsed?.systemWalletsTable },
      bankAccountsTable: { ...initialState, ...parsed?.bankAccountsTable },
      networksTable: { ...initialState, ...parsed?.networksTable },
      affiliatePartnersTable: {
        ...initialState,
        ...parsed?.affiliatePartnersTable,
      },
      affiliateStatsMonthlyTable: {
        ...initialState,
        ...parsed?.affiliateStatsMonthlyTable,
      },
      bookkeepingTable: {
        ...initialState,
        ...parsed?.bookkeepingTable,
      },
      feeStructuresTable: {
        ...initialState,
        ...parsed?.feeStructuresTable,
      },
      finanstilsynetSamplingTable: {
        ...initialState,
        ...parsed?.finanstilsynetSamplingTable,
      },
      finanstilsynetBreakdownTable: {
        ...initialState,
        ...parsed?.finanstilsynetBreakdownTable,
      },
      veliWealthManagersTable: {
        ...initialState,
        ...parsed?.veliWealthManagersTable,
      },
      veliClientsTable: {
        ...initialState,
        ...parsed?.veliClientsTable,
      },
      teamTable: {
        ...initialState,
        ...parsed?.teamTable,
      },
    }
    return session ? { ...newState, session } : newState
  }

  return session ? { session } : undefined
}

const rootReducer = {
  session: sessionReducer,
  currenciesTable: currenciesTableReducer,
  transfersTable: transfersTableReducer,
  fundDepositsTable: fundDepositsTableReducer,
  clientsTable: clientsTableReducer,
  systemWalletsTable: systemWalletsTableReducer,
  bankAccountsTable: bankAccountsTableReducer,
  networksTable: networksTableReducer,
  affiliatePartnersTable: affiliatePartnersTableReducer,
  affiliateStatsMonthlyTable: affiliateStatsMonthlyTableReducer,
  bookkeepingTable: bookkeepingTableReducer,
  feeStructuresTable: feeStructuresTableReducer,
  finanstilsynetSamplingTable: finanstilsynetSamplingTableReducer,
  finanstilsynetBreakdownTable: finanstilsynetBreakdownTableReducer,
  veliWealthManagersTable: veliWealthManagersTableReducer,
  veliClientsTable: veliClientsTableReducer,
  teamTable: teamTableReducer,
  [coreApi.reducerPath]: coreApi.reducer,
  [publicApi.reducerPath]: publicApi.reducer,
}

// Store factory. The app uses the singleton below (re-hydrated from
// local/session storage); tests build an isolated store per test via
// `setupStore()` so RTK Query cache and slice state never leak between them.
export const setupStore = (
  preloadedState?: ReturnType<typeof reHydrateStore>
) => {
  const store = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat([
        coreApi.middleware,
        publicApi.middleware,
        rtkQueryErrorLogger,
        localStorageMiddleware,
        impersonationPersistenceMiddleware,
      ]),
  })
  // optional, but required for refetchOnFocus/refetchOnReconnect behaviors
  // see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
  setupListeners(store.dispatch)
  return store
}

const store = setupStore(reHydrateStore())

// Infer the `RootState` and `AppDispatch` types from the store itself
export type AppStore = ReturnType<typeof setupStore>
export type RootState = ReturnType<typeof store.getState>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch

export default store
